import { useState } from "react";
import { toast } from "sonner";
import { Mail, CheckCircle2, XCircle, Clock } from "lucide-react";
import api from "../../lib/api";

const LOG_LABELS = { admin_new: "Notificare admin", client_received: "Client — cerere primită", confirmation: "Client — confirmare", rejection: "Client — respingere", meeting_update: "Client — detalii actualizate" };
const STATUS_ICON = {
  sent: <CheckCircle2 className="w-3.5 h-3.5 text-forest" />,
  failed: <XCircle className="w-3.5 h-3.5 text-destructive" />,
  skipped: <Clock className="w-3.5 h-3.5 text-moss" />,
};

export function EmailHistory({ log = [] }) {
  if (!log.length) return <div className="text-xs text-muted-foreground" data-testid="email-history-empty">Niciun e-mail înregistrat pentru această rezervare.</div>;
  return (
    <ul className="space-y-2" data-testid="email-history">
      {[...log].reverse().map((e, i) => (
        <li key={i} className="flex items-start gap-2 text-xs" data-testid={`email-log-${e.type}-${e.status}`}>
          <span className="mt-0.5">{STATUS_ICON[e.status] || STATUS_ICON.skipped}</span>
          <div className="flex-1">
            <div className="text-forest">{LOG_LABELS[e.type] || e.type} <span className="text-moss">→ {e.to}</span></div>
            <div className="text-moss">{new Date(e.at).toLocaleString("ro-RO")} · {e.status === "sent" ? "trimis" : e.status === "failed" ? "EȘUAT" : "omis"}</div>
            {e.error && <div className="text-destructive break-words" data-testid="email-log-error">{e.error}</div>}
          </div>
        </li>
      ))}
    </ul>
  );
}

export function BookingActions({ booking, mode, onDone }) {
  const [meetingTime, setMeetingTime] = useState(booking.meeting_time || booking.preferred_time || "");
  const [address, setAddress] = useState(booking.meeting_address || "");
  const [mapsUrl, setMapsUrl] = useState(booking.meeting_maps_url || "");
  const [reason, setReason] = useState(booking.rejection_reason || "");
  const [busy, setBusy] = useState(false);
  const confirmedBefore = !!booking.confirmation_sent_at;
  const rejectedBefore = !!booking.rejection_sent_at;

  const mapsInvalid = !!mapsUrl.trim() && !/^https:\/\/\S+$/.test(mapsUrl.trim());
  const detailsReady = !!meetingTime && address.trim().length >= 3 && !mapsInvalid;
  const details = { meeting_time: meetingTime, meeting_address: address.trim(), meeting_maps_url: mapsUrl.trim() || null };

  const run = async (path, body, label, method = "post") => {
    setBusy(true);
    try {
      const r = await api[method](`/bookings/${booking.id}/${path}`, body);
      const st = r.data.email_status;
      if (st === "not_sent") toast.success("Detaliile au fost salvate. Niciun e-mail nu a fost trimis.");
      else if (st === "in_progress") toast.info("O actualizare este deja în curs de trimitere.");
      else if (st === "sent") toast.success(`${label} trimisă către ${booking.email}.`);
      else if (st === "already_sent") toast.info(`${label} a fost deja trimisă — nu s-a retrimis.`);
      else toast.error(`${label}: e-mailul NU a plecat (${st}). Vezi istoricul.`);
      onDone(r.data.booking);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map((x) => x.msg).join(" · ") : d || "Eroare la trimitere.");
    } finally {
      setBusy(false);
    }
  };

  if (mode === "reject") {
    return (
      <div className="border border-destructive/40 bg-white p-4 space-y-3" data-testid="reject-panel">
        <div className="text-[10px] uppercase tracking-wider-3 text-destructive">Respinge și anunță clientul</div>
        <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motiv (opțional) — apare în e-mailul către client" data-testid="reject-reason" className="w-full border border-border px-3 py-2 text-sm outline-none focus:border-forest" />
        <div className="flex gap-2">
          <button disabled={busy} onClick={() => run("reject", { reason }, "Respingerea")} data-testid="reject-send-btn" className="btn-lift flex-1 bg-destructive text-white py-3 text-xs uppercase tracking-wider-3 disabled:opacity-60">
            {rejectedBefore ? "Marchează respinsă (fără e-mail)" : "Respinge + trimite e-mail"}
          </button>
          {rejectedBefore && (
            <button disabled={busy} onClick={() => run("reject", { reason, resend: true }, "Respingerea")} data-testid="reject-resend-btn" className="btn-lift border border-destructive text-destructive px-4 py-3 text-xs uppercase tracking-wider-3 disabled:opacity-60">Retrimite</button>
          )}
        </div>
        <div className="text-xs text-muted-foreground">Limba e-mailului: {(booking.lang || "ro").toUpperCase()} · e-mailul pleacă o singură dată, indiferent de câte click-uri.</div>
      </div>
    );
  }

  const field = "w-full border border-border px-3 py-2 text-sm outline-none focus:border-forest";
  const label = "text-[10px] uppercase tracking-wider-3 text-moss block mb-1";
  return (
    <div className="border border-forest/30 bg-white p-4 space-y-3" data-testid="confirm-panel">
      <div className="text-[10px] uppercase tracking-wider-3 text-forest">{confirmedBefore ? "Detalii de întâlnire (rezervare confirmată)" : "Confirmă și trimite detaliile finale"}</div>
      <label className="block">
        <span className={label}>Ora finală de întâlnire *{booking.preferred_time ? ` (clientul a cerut ${booking.preferred_time})` : " (clientul nu a cerut o oră)"}</span>
        <input type="time" value={meetingTime} onChange={(e) => setMeetingTime(e.target.value)} data-testid="meeting-time-input" className={field} />
      </label>
      <label className="block">
        <span className={label}>Adresa exactă de întâlnire *</span>
        <textarea rows={2} value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Ex.: strada, nr., reper — doar pentru această rezervare" data-testid="meeting-address-input" className={field} />
      </label>
      <label className="block">
        <span className={label}>Link Google Maps (opțional)</span>
        <input type="url" value={mapsUrl} onChange={(e) => setMapsUrl(e.target.value)} placeholder="https://maps.app.goo.gl/..." data-testid="meeting-maps-input" className={field} />
        {mapsInvalid && <span className="text-xs text-destructive" data-testid="meeting-maps-error">Linkul trebuie să înceapă cu https://</span>}
      </label>
      {!detailsReady && <div className="text-xs text-destructive" data-testid="meeting-details-missing">Completează ora și adresa exactă pentru a putea trimite e-mailul.</div>}
      {!confirmedBefore ? (
        <button disabled={busy || !detailsReady} onClick={() => run("confirm", details, "Confirmarea")} data-testid="confirm-send-btn" className="btn-lift w-full bg-forest text-bone py-3 text-xs uppercase tracking-wider-3 disabled:opacity-60">Confirmă + trimite e-mail</button>
      ) : (
        <div className="space-y-2">
          <div className="flex gap-2">
            <button disabled={busy || !detailsReady} onClick={() => run("meeting", details, "Salvarea", "put")} data-testid="meeting-save-btn" className="btn-lift flex-1 border border-forest text-forest py-3 text-xs uppercase tracking-wider-3 disabled:opacity-60">Salvează (fără e-mail)</button>
            <button disabled={busy || !detailsReady || booking.status !== "confirmed"} onClick={() => run("meeting-update", details, "Actualizarea")} data-testid="meeting-update-send-btn" className="btn-lift flex-1 bg-forest text-bone py-3 text-xs uppercase tracking-wider-3 disabled:opacity-60 flex items-center justify-center gap-2"><Mail className="w-3.5 h-3.5" /> Trimite detaliile actualizate</button>
          </div>
          {booking.status !== "confirmed" && <div className="text-xs text-destructive" data-testid="meeting-update-status-note">Actualizarea se poate trimite doar cât timp rezervarea are statusul „confirmată”.</div>}
          <button disabled={busy || !detailsReady} onClick={() => run("confirm", { ...details, resend: true }, "Confirmarea")} data-testid="confirm-resend-btn" className="text-xs text-moss underline disabled:opacity-60">Retrimite e-mailul de confirmare original</button>
        </div>
      )}
      <div className="text-xs text-muted-foreground">Limba e-mailului: {(booking.lang || "ro").toUpperCase()} · instrucțiunile generale pentru {booking.pickup_location} apar doar ca informații suplimentare. Editarea câmpurilor nu trimite niciun e-mail automat.</div>
    </div>
  );
}
